import { RefereeDutiesListTable } from '~/pages/referee-duties/list/components/referee-duties-list-table';
import { RefereeDutiesForm } from '~/pages/referee-duties/components/referee-duties-form';
import { useCompetitionRegistrationListQuery } from '../../detail/registration/queries';
import { RefereeSearchSelect } from '~/pages/referee/components/referee-search-select';
import { Schema as GroupsSchema } from '../../groups/components/groups-form.schema';
import { RegistrationListFilter } from './components/registration-list-filter';
import { GroupsListTable } from '../../groups/components/groups-list-table';
import { GroupsForm } from '../../groups/components/groups-form';
import { AddLicensedLifter } from './components/result-add-lifter';
import { PageLayout } from '~/components/PageLayout/PageLayout';
import { useCreateGroupMutation } from '../../groups/mutations';
import { Pagination } from '~/components/Pagination/Pagination';
import { withPrivateRoute } from '~/hocs/withPrivateRoute';
import { useCompetitionResultListQuery } from './queries';
import { ResultGrid } from './components/result-grid';
import { Skeleton } from '~/components/Skeleton/Skeleton';
import { ResultTable } from './components/result-table';
import { Heading } from '~/components/Heading/Heading';
import { PropsWithChildren, useState } from 'react';
import { useFilterForm } from '~/hooks/useFilterForm';
import { useCompetitionQuery } from '../../queries';
import { Panel } from '~/components/Panel/Panel';
import { Grid } from '~/components/Grid/Grid';
import { useParams } from '~/hooks/useParams';
import { Fragment } from 'react/jsx-runtime';
import { querySchema } from './query-schema';
import styles from './page.module.css';
import { idParamSchema } from '~/schemas';
import { toast } from 'react-toastify';
import { Result } from '~/apis/ssf';

// eslint-disable-next-line react-refresh/only-export-components
function Hint({ children }: PropsWithChildren) {
  return <p className={styles.hint}>{children}</p>;
}

interface ResultGroup {
  heading: string;
  results: Result[];
}

function groupResults(results: Result[], isTeamComp: boolean) {
  return results.reduce<Record<string, ResultGroup>>((acc, result) => {
    const key = isTeamComp
      ? `team_${result.team.id}`
      : `${result.competition_type.description}_${result.age_category.label}_${result.weight_class.name}`;

    if (!acc[key]) {
      const heading = isTeamComp
        ? `${result.team.division.name} — ${result.team.name}`
        : `${result.competition_type.description} - ${result.age_category.label} ${result.weight_class.name}`;
      acc[key] = { heading, results: [] };
    }

    acc[key].results.push(result);
    return acc;
  }, {});
}

export const Page = withPrivateRoute(() => {
  const { id } = useParams(idParamSchema.parse);
  const { data: competition } = useCompetitionQuery(id);
  const isTeamComp = competition?.is_team_comp ?? false;

  const { values } = useFilterForm(querySchema.parse);
  const { data, isLoading } = useCompetitionRegistrationListQuery({
    competition: id,
    page_size: values.pageSize,
    page: values.pageIndex,
    search: values.search,
    exclude_with_result: true,
    competition_type: values.competition_type_id,
    editable: true,
  });
  const { data: results, isLoading: isResultsLoading } = useCompetitionResultListQuery({
    competition: id,
    page_size: 999,
  });

  const groupedResults = groupResults(results?.results ?? [], isTeamComp);

  const mutation = useCreateGroupMutation();

  async function onSubmitGroup(data: GroupsSchema) {
    await mutation.mutateAsync(data);
    toast.success('Grupp skapad');
  }

  const [referee, setReferee] = useState<[string, number] | undefined>(undefined);

  return (
    <Fragment>
      {competition ? <PageLayout.Heading text={`Hantera resultat - ${competition.name}`} /> : <Skeleton />}
      <PageLayout.Section>
        <PageLayout.SubHeading>Skapa grupp</PageLayout.SubHeading>
        <Hint>Börja med att skapa en eller flera grupper. Du kan sedan registrera resultat för varje grupp.</Hint>
        <GroupsForm
          onSubmit={onSubmitGroup}
          mode="create"
          isLoading={mutation.isPending}
          competitionStartDate={competition?.start_date ?? ''}
        />
        <PageLayout.SubHeading>Befintliga grupper</PageLayout.SubHeading>
        <GroupsListTable />
      </PageLayout.Section>

      <PageLayout.Section>
        <PageLayout.SubHeading>
          <span className={styles.subHeading}>
            Registrerade lyftare
            {data && <span className={styles.subHeadingMeta}>{data.count} utan resultat</span>}
          </span>
        </PageLayout.SubHeading>
        <Hint>Fyll i resultat direkt i tabellen och spara en lyftare i taget.</Hint>
        <RegistrationListFilter isTeamComp={isTeamComp} />
        <ResultGrid lifters={data?.results} competitionId={id} isTeamComp={isTeamComp} isLoading={isLoading} />
        <Pagination count={data?.count} />

        <div className={styles.addLifter}>
          <Heading as="h5" style={{ marginTop: 0 }}>
            Lägg till licensierad lyftare
          </Heading>
          <Hint>
            Saknas lyftaren bland de anmälda? Lägg till personen genom att söka bland alla licensierade lyftare.
          </Hint>
          <AddLicensedLifter
            competitionId={id}
            isTeamComp={isTeamComp}
            competitionStartDate={competition?.start_date ?? undefined}
            disabled={!data}
          />
        </div>
      </PageLayout.Section>

      <PageLayout.Section>
        <PageLayout.SubHeading>Registerade resultat</PageLayout.SubHeading>

        {!isResultsLoading && Object.keys(groupedResults).length === 0 ? (
          <Panel variant="informative" size="sm">
            <p>Inga resultat är registrerade ännu. Registrera resultat för de anmälda lyftarna ovan.</p>
          </Panel>
        ) : (
          Object.entries(groupedResults).map(([key, group]) => (
            <ResultTable key={key} results={group.results} isLoading={isLoading} heading={group.heading} />
          ))
        )}
      </PageLayout.Section>

      <PageLayout.Section>
        <PageLayout.SubHeading>Domaruppdrag</PageLayout.SubHeading>
        <Hint>Sök efter domare och tilldela domaruppdrag.</Hint>
        <Grid>
          <Grid.Item cols={{ lg: 4, sm: 12 }}>
            <RefereeSearchSelect
              onChange={([referee, refereeId]) => {
                setReferee([referee, Number(refereeId)]);
              }}
              query={{
                status: ['active', 'warned'],
                ordering: 'person__first_name,person__last_name',
              }}
              placeholder="Sök efter domare..."
              name="referee"
              value={referee}
            />
          </Grid.Item>
        </Grid>
        {referee && (
          <>
            <Heading as="h4">{`${referee[0]}`}</Heading>
            <RefereeDutiesForm refereeId={referee[1]} onFinish={() => setReferee(undefined)} />
          </>
        )}
      </PageLayout.Section>

      <PageLayout.Section>
        <PageLayout.SubHeading>Registrerade domaruppdrag</PageLayout.SubHeading>
        <RefereeDutiesListTable competitionId={id} />
      </PageLayout.Section>
    </Fragment>
  );
});
